/**
 * Heartbeats Store - Gestion d'état centralisée avec Zustand
 * Remplace les useState dispersés dans FlammeLPE.js
 * 
 * Migration: Utilise maintenant /api/heartbeats/* au lieu de /api/flamme/*
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_PREFIX = '/api/heartbeats'; // Nouveau prefix

// Store principal pour Heartbeats
export const useFlammeStore = create(
  devtools(
    persist(
      (set, get) => ({
        // ============================================
        // ÉTAT DU PROFIL
        // ============================================
        profile: null,
        isProfileLoading: false,
        profileError: null,

        setProfile: (profile) => set({ profile, profileError: null }),
        
        fetchProfile: async (customerId) => {
          if (!customerId) return;
          set({ isProfileLoading: true, profileError: null });
          try {
            const res = await fetch(`${API_URL}${API_PREFIX}/profile/${customerId}`);
            if (res.ok) {
              const data = await res.json();
              set({ profile: data, isProfileLoading: false });
              return data;
            } else if (res.status === 404) {
              set({ profile: null, isProfileLoading: false });
              return null;
            }
            throw new Error('Erreur lors du chargement du profil');
          } catch (error) {
            set({ profileError: error.message, isProfileLoading: false });
            return null;
          }
        },

        updateProfile: async (customerId, updates) => {
          try {
            const res = await fetch(`${API_URL}${API_PREFIX}/profile/${customerId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(updates),
            });
            if (res.ok) {
              const current = get().profile;
              set({ profile: { ...current, ...updates } });
              return true;
            }
            return false;
          } catch (error) {
            console.error('Erreur mise à jour profil:', error);
            return false;
          }
        },

        // ============================================
        // COURONNES
        // ============================================
        crowns: 0,
        
        setCrowns: (crowns) => set({ crowns }),
        
        fetchCrowns: async (customerId) => {
          if (!customerId) return;
          try {
            const res = await fetch(`${API_URL}/api/heartbeats/crowns/${customerId}`);
            if (res.ok) {
              const data = await res.json();
              set({ crowns: data.crowns || 0 });
              return data.crowns;
            }
          } catch (error) {
            console.error('Erreur récupération couronnes:', error);
          }
          return 0;
        },

        addCrowns: (amount) => set((state) => ({ crowns: state.crowns + amount })),
        removeCrowns: (amount) => set((state) => ({ crowns: Math.max(0, state.crowns - amount) })),

        // ============================================
        // MATCHS & MESSAGES
        // ============================================
        matches: [],
        activeMatchId: null,
        messages: {}, // matchId -> messages[]
        unreadCounts: {}, // matchId -> count

        setMatches: (matches) => set({ matches }),
        setActiveMatch: (matchId) => set({ activeMatchId: matchId }),
        
        fetchMatches: async (customerId) => {
          if (!customerId) return;
          try {
            const res = await fetch(`${API_URL}/api/heartbeats/matches/${customerId}`);
            if (res.ok) {
              const data = await res.json();
              set({ matches: data.matches || [] });
              return data.matches;
            }
          } catch (error) {
            console.error('Erreur récupération matchs:', error);
          }
          return [];
        },

        addMessage: (matchId, message) => set((state) => ({
          messages: {
            ...state.messages,
            [matchId]: [...(state.messages[matchId] || []), message]
          }
        })),

        setMessages: (matchId, messages) => set((state) => ({
          messages: { ...state.messages, [matchId]: messages }
        })),

        incrementUnread: (matchId) => set((state) => ({
          unreadCounts: {
            ...state.unreadCounts,
            [matchId]: (state.unreadCounts[matchId] || 0) + 1
          }
        })),

        clearUnread: (matchId) => set((state) => ({
          unreadCounts: { ...state.unreadCounts, [matchId]: 0 }
        })),

        // ============================================
        // DÉCOUVERTE (SWIPE)
        // ============================================
        discoverProfiles: [],
        currentProfileIndex: 0,
        isDiscoverLoading: false,

        setDiscoverProfiles: (profiles) => set({ discoverProfiles: profiles, currentProfileIndex: 0 }),

        fetchDiscoverProfiles: async (customerId) => {
          if (!customerId) return;
          set({ isDiscoverLoading: true });
          try {
            const res = await fetch(`${API_URL}/api/heartbeats/discover/${customerId}`);
            if (res.ok) {
              const data = await res.json();
              set({ 
                discoverProfiles: data.profiles || [], 
                currentProfileIndex: 0,
                isDiscoverLoading: false 
              });
              return data.profiles;
            }
          } catch (error) {
            console.error('Erreur découverte profils:', error);
          }
          set({ isDiscoverLoading: false });
          return [];
        },

        nextProfile: () => set((state) => ({
          currentProfileIndex: Math.min(
            state.currentProfileIndex + 1,
            state.discoverProfiles.length - 1
          )
        })),

        removeCurrentProfile: () => set((state) => ({
          discoverProfiles: state.discoverProfiles.filter(
            (_, i) => i !== state.currentProfileIndex
          ),
          currentProfileIndex: Math.max(0, state.currentProfileIndex)
        })),

        // ============================================
        // PRÉSENCE & EN LIGNE
        // ============================================
        onlineUsers: [],
        usersAtStore: [],

        setOnlineUsers: (users) => set({ onlineUsers: users }),
        addOnlineUser: (userId) => set((state) => ({
          onlineUsers: [...new Set([...state.onlineUsers, userId])]
        })),
        removeOnlineUser: (userId) => set((state) => ({
          onlineUsers: state.onlineUsers.filter(id => id !== userId)
        })),

        setUsersAtStore: (users) => set({ usersAtStore: users }),
        addUserAtStore: (userId) => set((state) => ({
          usersAtStore: [...new Set([...state.usersAtStore, userId])]
        })),
        removeUserAtStore: (userId) => set((state) => ({
          usersAtStore: state.usersAtStore.filter(id => id !== userId)
        })),

        // ============================================
        // CRUSHES
        // ============================================
        crushes: [], // Crushes envoyés
        receivedCrushes: [], // Crushes reçus

        setCrushes: (crushes) => set({ crushes }),
        setReceivedCrushes: (received) => set({ receivedCrushes: received }),
        
        addCrush: (crush) => set((state) => ({
          crushes: [...state.crushes, crush]
        })),

        fetchCrushes: async (customerId) => {
          if (!customerId) return;
          try {
            const res = await fetch(`${API_URL}/api/heartbeats/crushes/${customerId}`);
            if (res.ok) {
              const data = await res.json();
              set({ crushes: data || [] });
              return data;
            }
          } catch (error) {
            console.error('Erreur récupération crushes:', error);
          }
          return [];
        },

        fetchReceivedCrushes: async (customerId) => {
          if (!customerId) return;
          try {
            const res = await fetch(`${API_URL}/api/heartbeats/crushes/received/${customerId}`);
            if (res.ok) {
              const data = await res.json();
              set({ receivedCrushes: data || [] });
              return data;
            }
          } catch (error) {
            console.error('Erreur récupération crushes reçus:', error);
          }
          return [];
        },

        // ============================================
        // UI STATE
        // ============================================
        activeTab: 'discover',
        showProfileEditor: false,
        showSettings: false,
        isAtStore: false,
        isVisible: false,

        setActiveTab: (tab) => set({ activeTab: tab }),
        setShowProfileEditor: (show) => set({ showProfileEditor: show }),
        setShowSettings: (show) => set({ showSettings: show }),
        setIsAtStore: (value) => set({ isAtStore: value }),
        setIsVisible: (value) => set({ isVisible: value }),

        // ============================================
        // NOTIFICATIONS
        // ============================================
        notifications: [],
        
        addNotification: (notification) => set((state) => ({
          notifications: [
            { id: Date.now(), timestamp: new Date().toISOString(), ...notification },
            ...state.notifications.slice(0, 49) // Garder max 50 notifications
          ]
        })),
        
        clearNotifications: () => set({ notifications: [] }),
        
        removeNotification: (id) => set((state) => ({
          notifications: state.notifications.filter(n => n.id !== id)
        })),

        // ============================================
        // RESET
        // ============================================
        reset: () => set({
          profile: null,
          crowns: 0,
          matches: [],
          activeMatchId: null,
          messages: {},
          unreadCounts: {},
          discoverProfiles: [],
          currentProfileIndex: 0,
          onlineUsers: [],
          usersAtStore: [],
          crushes: [],
          receivedCrushes: [],
          notifications: [],
          activeTab: 'discover',
          showProfileEditor: false,
          showSettings: false,
          isAtStore: false,
          isVisible: false,
        }),
      }),
      {
        name: 'heartbeats-storage',
        partialize: (state) => ({
          // Ne persister que certains états
          activeTab: state.activeTab,
        }),
      }
    ),
    { name: 'FlammeStore' }
  )
);

// Sélecteurs pour optimiser les re-renders
export const selectProfile = (state) => state.profile;
export const selectCrowns = (state) => state.crowns;
export const selectMatches = (state) => state.matches;
export const selectActiveMatch = (state) => 
  state.matches.find(m => m.id === state.activeMatchId);
export const selectCurrentDiscoverProfile = (state) =>
  state.discoverProfiles[state.currentProfileIndex];
export const selectUnreadTotal = (state) =>
  Object.values(state.unreadCounts).reduce((a, b) => a + b, 0);
export const selectCrushes = (state) => state.crushes;
export const selectReceivedCrushes = (state) => state.receivedCrushes;
export const selectIsVisible = (state) => state.isVisible;
export const selectMessages = (matchId) => (state) => state.messages[matchId] || [];

export default useFlammeStore;
